import {
  AlertCircle,
  Check,
  FileStack,
  Filter,
  Pencil,
  RefreshCw,
  Search,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ApiError } from "@/lib/api/client";
import type { JobOut } from "@/lib/api/jobs";
import { cn } from "@/lib/cn";

import DeleteJobConfirmModal from "./DeleteJobConfirmModal";
import StatusBadge from "./StatusBadge";
import { formatDateTime, progressPercent, useRelativeTime } from "./format";
import { useJobs, useUpdateJob } from "./useJobs";

type StatusFilter = "all" | "pending" | "processing" | "completed" | "failed";

/**
 * 상태 필터 옵션 목록.
 *
 * 라벨은 ``jobs.list.filter.*`` 키 — 화면에서 ``t()`` 로 렌더 시점에 해석.
 * 옵션 정의 자체는 string literal 을 가지지 않아야 한다 (i18n 의존성 회피).
 */
const STATUS_OPTIONS: Array<{ value: StatusFilter; i18nKey: string }> = [
  { value: "all", i18nKey: "jobs.list.filter.all" },
  { value: "pending", i18nKey: "jobs.list.filter.pending" },
  { value: "processing", i18nKey: "jobs.list.filter.processing" },
  { value: "completed", i18nKey: "jobs.list.filter.completed" },
  { value: "failed", i18nKey: "jobs.list.filter.failed" },
];

export default function JobsListPage() {
  const { t } = useTranslation();
  const formatRelative = useRelativeTime();
  const { data: jobs, isLoading, isError, error, refetch, isFetching } = useJobs();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  // 삭제 확인 모달 — 한 번에 한 Job 만 처리 (null 이면 닫힘).
  const [deleteTarget, setDeleteTarget] = useState<JobOut | null>(null);
  // 제목(배치명) 인라인 편집 — 한 번에 한 Job.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const updateMutation = useUpdateJob();

  const startEdit = (job: JobOut) => {
    setEditingId(job.id);
    setEditingName(job.batch_name ?? "");
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditingName("");
  };
  const saveEdit = (jobId: string) => {
    const next = editingName.trim();
    updateMutation.mutate(
      { jobId, batchName: next.length > 0 ? next : null },
      { onSuccess: cancelEdit },
    );
  };

  const filteredJobs = useMemo(() => {
    if (!jobs) return [];
    const q = search.trim().toLowerCase();
    return jobs.filter((job) => {
      if (status !== "all" && job.status !== status) return false;
      if (q) {
        const inBatch = (job.batch_name ?? "").toLowerCase().includes(q);
        const inId = job.id.toLowerCase().includes(q);
        if (!inBatch && !inId) return false;
      }
      return true;
    });
  }, [jobs, search, status]);

  const resetFilters = () => {
    setSearch("");
    setStatus("all");
  };

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{t("jobs.list.title")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("jobs.list.description")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
            aria-label={t("common.actions.refresh")}
          >
            <RefreshCw className={cn("h-3.5 w-3.5", isFetching && "animate-spin")} />
            {t("common.actions.refresh")}
          </button>
          <Link
            to="/upload"
            className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow transition hover:bg-blue-700"
          >
            <Upload className="h-3.5 w-3.5" />
            {t("jobs.list.newUpload")}
          </Link>
        </div>
      </header>

      {/* 검색/필터 바 — 데이터 있을 때만 노출 */}
      {!isLoading && !isError && jobs && jobs.length > 0 && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("jobs.list.search.placeholder")}
              className="w-full rounded-md border border-slate-300 bg-white py-1.5 pl-9 pr-9 text-sm placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              aria-label={t("jobs.list.search.ariaLabel")}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                aria-label={t("jobs.list.search.clear")}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="relative">
            <Filter className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as StatusFilter)}
              className="appearance-none rounded-md border border-slate-300 bg-white py-1.5 pl-8 pr-8 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
              aria-label={t("jobs.list.filter.ariaLabel")}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.i18nKey)}
                </option>
              ))}
            </select>
          </div>
          <span className="whitespace-nowrap text-xs text-slate-500 sm:ml-1">
            {t("jobs.list.count", { visible: filteredJobs.length, total: jobs.length })}
          </span>
        </div>
      )}

      {/* 본문 */}
      {isLoading ? (
        <SkeletonRows />
      ) : isError ? (
        <ErrorBox error={error} />
      ) : !jobs || jobs.length === 0 ? (
        <EmptyState />
      ) : filteredJobs.length === 0 ? (
        <NoResults onReset={resetFilters} />
      ) : (
        <ul className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          {filteredJobs.map((job, idx) => {
            const pct = progressPercent(
              job.file_count,
              job.success_count,
              job.rejected_count,
              job.error_count,
            );
            return (
              <li
                key={job.id}
                className={cn(
                  "group relative transition hover:bg-slate-50",
                  idx > 0 && "border-t border-slate-100",
                )}
              >
                {/* 전체 행 네비게이션 — stretched link (콘텐츠 뒤에 깔림). */}
                <Link
                  to={`/jobs/${job.id}`}
                  aria-label={job.batch_name || job.id.slice(0, 8)}
                  className="absolute inset-0 z-0"
                />
                {/*
                 * 콘텐츠 — 링크 위에 표시하되 클릭은 링크로 통과(pointer-events-none).
                 * 개별 인터랙티브 요소(연필 등)만 pointer-events-auto 로 되살린다.
                 */}
                <div className="pointer-events-none relative z-10 grid grid-cols-12 items-center gap-3 py-3 pl-4 pr-12 text-sm">
                  {/* 배치명 + ID + 제목 편집(연필) */}
                  <div className="col-span-4 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="truncate font-medium text-slate-900">
                        {job.batch_name || (
                          <span className="text-slate-400">
                            {t("common.placeholders.anonymous")}
                          </span>
                        )}
                      </p>
                      {editingId !== job.id && (
                        <button
                          type="button"
                          onClick={() => startEdit(job)}
                          className="pointer-events-auto shrink-0 rounded p-1 text-slate-300 opacity-0 transition hover:bg-blue-50 hover:text-blue-600 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-blue-300 group-hover:opacity-100"
                          aria-label={t("jobs.list.row.renameAriaLabel", {
                            name: job.batch_name || job.id.slice(0, 8),
                          })}
                          title={t("jobs.list.row.renameTitle")}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    <p className="mt-0.5 truncate text-[11px] text-slate-400">
                      #{job.id.slice(0, 8)}
                    </p>
                  </div>

                  {/* 상태 + 파일 카운트 + 검수완료 카운트 */}
                  <div className="col-span-3 flex items-center gap-2">
                    <StatusBadge status={job.status} />
                    <span className="text-xs text-slate-500">
                      {t("jobs.list.fileCount", { count: job.file_count })}
                    </span>
                    {job.status === "completed" && (
                      <span
                        className={cn(
                          "rounded px-1.5 py-0.5 text-[11px]",
                          job.reviewed_count >= job.file_count && job.file_count > 0
                            ? "bg-blue-100 text-blue-800"
                            : "bg-slate-100 text-slate-600",
                        )}
                        title={t("jobs.list.reviewedTitle")}
                      >
                        {t("jobs.list.reviewedCount", {
                          reviewed: job.reviewed_count,
                          total: job.file_count,
                        })}
                      </span>
                    )}
                  </div>

                  {/* 진행률 / 결과 */}
                  <div className="col-span-3">
                    {job.status === "completed" || job.status === "failed" ? (
                      <div className="flex items-center gap-1.5 text-[11px]">
                        {job.success_count > 0 && (
                          <span className="rounded bg-green-50 px-1.5 py-0.5 text-green-700">
                            ✓ {job.success_count}
                          </span>
                        )}
                        {job.rejected_count > 0 && (
                          <span className="rounded bg-amber-50 px-1.5 py-0.5 text-amber-700">
                            ⚠ {job.rejected_count}
                          </span>
                        )}
                        {job.error_count > 0 && (
                          <span className="rounded bg-red-50 px-1.5 py-0.5 text-red-700">
                            ✗ {job.error_count}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div>
                        <div className="mb-1 flex justify-between text-[10px] text-slate-400">
                          <span>{pct}%</span>
                        </div>
                        <div className="h-1 overflow-hidden rounded-full bg-slate-100">
                          <div
                            className="h-full bg-blue-500 transition-[width] duration-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* 생성 시각 */}
                  <div className="col-span-2 text-right">
                    <p className="text-xs text-slate-600" title={formatDateTime(job.created_at)}>
                      {formatRelative(job.created_at)}
                    </p>
                  </div>
                </div>

                {/*
                 * 제목 인라인 편집 오버레이 — ``<Link>`` 위(z-20)에 절대배치해
                 * 클릭이 링크로 전파(페이지 이동)되지 않도록 한다. 편집 중인
                 * 행에만 렌더된다.
                 */}
                {editingId === job.id && (
                  <div className="absolute left-4 top-1/2 z-20 flex w-1/3 min-w-[240px] -translate-y-1/2 items-center gap-1.5">
                    <input
                      autoFocus
                      value={editingName}
                      onChange={(e) => setEditingName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          saveEdit(job.id);
                        } else if (e.key === "Escape") {
                          e.preventDefault();
                          cancelEdit();
                        }
                      }}
                      placeholder={t("jobs.list.row.renamePlaceholder")}
                      maxLength={200}
                      disabled={updateMutation.isPending}
                      className="w-full rounded-md border border-blue-400 px-2 py-1 text-sm text-slate-900 focus:outline-none focus:ring-1 focus:ring-blue-200"
                    />
                    <button
                      type="button"
                      onClick={() => saveEdit(job.id)}
                      disabled={updateMutation.isPending}
                      className="rounded p-1.5 text-emerald-600 transition hover:bg-emerald-50 disabled:opacity-50"
                      aria-label={t("common.actions.save")}
                      title={t("common.actions.save")}
                    >
                      <Check className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={cancelEdit}
                      disabled={updateMutation.isPending}
                      className="rounded p-1.5 text-slate-400 transition hover:bg-slate-100 disabled:opacity-50"
                      aria-label={t("common.actions.cancel")}
                      title={t("common.actions.cancel")}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}

                {/*
                 * 삭제 버튼 — stretched link(z-0) 위(z-10)에 절대배치. ``<li>`` 가
                 * ``relative`` 라서 absolute positioning 으로 띄움. hover/focus
                 * 시에만 opacity 1 로 노출 (D4 = trash_on_hover).
                 */}
                <button
                  type="button"
                  onClick={() => setDeleteTarget(job)}
                  className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded p-1.5 text-slate-400 opacity-0 transition hover:bg-red-50 hover:text-red-600 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-red-300 group-hover:opacity-100"
                  aria-label={t("jobs.list.row.deleteAriaLabel", {
                    name: job.batch_name || job.id.slice(0, 8),
                  })}
                  title={t("jobs.list.row.deleteTitle")}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {deleteTarget ? (
        <DeleteJobConfirmModal job={deleteTarget} onClose={() => setDeleteTarget(null)} />
      ) : null}
    </div>
  );
}

function SkeletonRows() {
  const { t } = useTranslation();
  return (
    <ul
      className="overflow-hidden rounded-lg border border-slate-200 bg-white"
      aria-label={t("jobs.list.loadingAria")}
    >
      {[0, 1, 2].map((i) => (
        <li
          key={i}
          className={cn("grid grid-cols-12 gap-3 px-4 py-3", i > 0 && "border-t border-slate-100")}
        >
          <div className="col-span-4 space-y-1.5">
            <div className="h-3 w-2/3 animate-pulse rounded bg-slate-100" />
            <div className="h-2 w-1/3 animate-pulse rounded bg-slate-100" />
          </div>
          <div className="col-span-3 h-4 w-20 animate-pulse rounded-full bg-slate-100" />
          <div className="col-span-3 h-2 w-full animate-pulse rounded bg-slate-100" />
          <div className="col-span-2 h-3 w-16 animate-pulse rounded bg-slate-100" />
        </li>
      ))}
    </ul>
  );
}

function ErrorBox({ error }: { error: unknown }) {
  const msg = error instanceof ApiError ? error.detail : (error as Error).message;
  return (
    <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
      <span>{msg}</span>
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-white py-16 text-center">
      <FileStack className="mx-auto h-10 w-10 text-slate-300" />
      <p className="mt-3 text-sm font-medium text-slate-700">{t("jobs.list.empty.title")}</p>
      <p className="mt-1 text-xs text-slate-400">{t("jobs.list.empty.description")}</p>
      <Link
        to="/upload"
        className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow hover:bg-blue-700"
      >
        <Upload className="h-3.5 w-3.5" />
        {t("jobs.list.empty.action")}
      </Link>
    </div>
  );
}

function NoResults({ onReset }: { onReset: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-white py-12 text-center">
      <Search className="mx-auto h-8 w-8 text-slate-300" />
      <p className="mt-3 text-sm font-medium text-slate-700">{t("jobs.list.noResults.title")}</p>
      <p className="mt-1 text-xs text-slate-400">{t("jobs.list.noResults.description")}</p>
      <button
        type="button"
        onClick={onReset}
        className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-xs text-slate-700 transition hover:bg-slate-50"
      >
        {t("jobs.list.noResults.reset")}
      </button>
    </div>
  );
}
